// What the member has on this chapter, in the study rail beside the text:
// their own notes and bookmarks, each a jump back to the verse it is about.
//
// Highlights are not listed. They are already visible where they matter — on
// the words — and a list of coloured fragments beside the passage would say
// nothing the page is not already saying.
import { markdownExcerpt, type BookmarkView, type HighlightColor, type MemberNoteView } from "@bibleunleashed/shared";
import { Icon } from "@/components/Icon";

const DOT: Record<HighlightColor, string> = {
  yellow: "var(--hl-y)",
  green: "var(--hl-g)",
  blue: "var(--hl-b)",
  pink: "var(--hl-p)",
};

const verseOf = (ref: string) => ref.split(".")[2] ?? "";

export function MyMarks({
  signedIn,
  notes,
  bookmarks,
  onOpenNote,
  onJump,
}: {
  signedIn: boolean;
  notes: MemberNoteView[];
  bookmarks: BookmarkView[];
  onOpenNote: (ref: string) => void;
  /** Scrolls to a verse. Not an anchor: only a block's first verse has an id. */
  onJump: (ref: string) => void;
}) {
  if (!signedIn) {
    return (
      <div className="rounded-lg border border-rule bg-paper-2/60 px-4 py-3.5">
        <h2 className="mb-1.5 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase">Your notes</h2>
        <p className="font-serif text-[14.5px] leading-relaxed text-ink-3">
          <a href="/login" className="text-accent">
            Sign in
          </a>{" "}
          to highlight verses, write your own notes and keep bookmarks. Free, and they follow you between devices.
        </p>
      </div>
    );
  }
  if (!notes.length && !bookmarks.length) {
    return (
      <div>
        <h2 className="mb-3 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase">Your notes</h2>
        <p className="font-serif text-[14.5px] leading-relaxed text-ink-3 italic">
          Right-click — or press and hold — any verse to highlight it, write a note on it, or bookmark it.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {notes.length > 0 && (
        <div>
          <h2 className="mb-3 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase">
            Your notes <span className="text-ink-3/70">({notes.length})</span>
          </h2>
          <ul className="space-y-3">
            {notes.map((note) => (
              <li key={note.id}>
                <button
                  onClick={() => onOpenNote(note.ref)}
                  className="w-full rounded-md border-l-2 border-accent/40 bg-paper-2/60 py-2 pr-2 pl-3 text-left hover:border-accent"
                >
                  <span className="font-sans text-[10.5px] tracking-[0.08em] text-ink-3 uppercase">
                    v. {verseOf(note.ref)}
                  </span>
                  <span className="mt-0.5 block font-serif text-[14px] leading-snug text-ink-2">
                    {markdownExcerpt(note.bodyMd, 110)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {bookmarks.length > 0 && (
        <div>
          <h2 className="mb-3 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase">Bookmarks</h2>
          <ul className="space-y-2">
            {bookmarks.map((mark) => (
              <li key={mark.id}>
                <button
                  onClick={() => onJump(mark.ref)}
                  className="flex w-full items-center gap-2 text-left text-[14px] text-ink-2 hover:text-accent"
                >
                  <Icon name="bookmark" width="13" height="13" style={{ color: DOT[mark.color] }} className="shrink-0" />
                  <span className="truncate">{mark.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
